import { Component, inject, Output, EventEmitter, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { HlmButtonDirective } from "@spartan-ng/ui-button-helm";
import { HlmInputDirective } from "@spartan-ng/ui-input-helm";
import { HlmLabelDirective } from "@spartan-ng/ui-label-helm";
import { AuthService } from "../../../services/auth.service";
import { SignInEnum } from "../../../enums/authenticate.enum";
import { CognitoSignInResult } from "../../../interfaces/auth.interfaces";

/**
 * Component for TOTP verification during authentication
 */
@Component({
  selector: "auth-totp-verification",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    HlmButtonDirective,
    HlmInputDirective,
    HlmLabelDirective,
  ],
  template: `
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <h3 class="text-lg font-semibold">Verifica Autenticazione</h3>
        <p class="text-sm text-gray-600">
          Inserisci il codice a 6 cifre dalla tua app di autenticazione
        </p>
      </div>

      <div class="flex flex-col gap-4">
        <label hlmLabel for="totp-code">Codice di verifica</label>
        <input
          id="totp-code"
          type="text"
          placeholder="000000"
          maxlength="6"
          [formControl]="totpCodeControl"
          (input)="onCodeInput($event)"
          hlmInput
          class="text-center tracking-widest"
        />
        
        @if (errorMessage()) {
          <p class="text-sm text-red-600">{{ errorMessage() }}</p>
        }

        <div class="flex gap-2">
          <button
            type="button"
            variant="outline"
            (click)="onCancel()"
            hlmBtn
            class="flex-1"
          >
            Annulla
          </button>
          
          <button
            type="button"
            (click)="onVerify()"
            [disabled]="!totpCodeControl.valid || isLoading()"
            hlmBtn
            class="flex-1"
          >
            @if (isLoading()) {
              Verifica in corso...
            } @else {
              Verifica
            }
          </button>
        </div>
      </div>
    </div>
  `,
})
export class TotpVerificationComponent {
  /** Auth service for TOTP verification */
  private readonly _authService = inject(AuthService);

  /** Output event when verification is successful */
  @Output() verificationSuccess = new EventEmitter<CognitoSignInResult>();

  /** Output event when user cancels */
  @Output() verificationCancel = new EventEmitter<void>();

  /** Form control for TOTP code */
  public totpCodeControl = new FormControl("", {
    validators: [Validators.required, Validators.pattern(/^\d{6}$/)],
    nonNullable: true,
  });

  /** Loading state signal */
  public isLoading = signal(false);

  /** Error message signal */
  public errorMessage = signal<string | null>(null);

  /**
   * Handles code input to auto-submit when 6 digits are entered
   */
  public onCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    // Clear error when user starts typing
    if (this.errorMessage()) {
      this.errorMessage.set(null);
    }

    // Auto-verify when 6 digits are entered
    if (value.length === 6 && this.totpCodeControl.valid) {
      this.onVerify();
    }
  }

  /**
   * Verifies the TOTP code
   */
  public onVerify(): void {
    if (!this.totpCodeControl.valid || this.isLoading()) {
      return;
    }

    const totpCode = this.totpCodeControl.value;
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this._authService.completeTOTP$(totpCode).subscribe({
      next: (result) => {
        this.isLoading.set(false);
        if (result.type === SignInEnum.SUCCESS) {
          this.verificationSuccess.emit(result);
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        this.errorMessage.set(
          error?.result?.message || "Codice non valido. Riprova."
        );
        this.totpCodeControl.setValue("");
      },
    });
  }

  /**
   * Handles cancellation
   */
  public onCancel(): void {
    this._authService.clearCachedCredentials();
    this.verificationCancel.emit();
  }
}